import React, { useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { Button, CircularProgress } from "@material-ui/core";
import { Search, SlidersHorizontal } from "lucide-react";
import debounce from "lodash.debounce";
import styles from "./Header.module.scss";
import { useExportInventoryPdf } from "../../../../utils/react-query/queryInventory/queries";
import { useMyPermissions } from "../../../../utils/react-query/queryRbac/queries";

interface HeaderProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onFilterToggle: () => void;
  isFilterOpen: boolean;
}

export function Header({
  searchQuery,
  onSearchChange,
  onFilterToggle,
  isFilterOpen,
}: HeaderProps) {
  const history = useHistory();
  const { mutate: exportPdf, isLoading: isExporting } = useExportInventoryPdf();

  const {
    data: permissions = new Set<string>(),
    isLoading: permissionsLoading,
  } = useMyPermissions();

  const canDownloadPdf =
    !permissionsLoading && permissions.has("inventory:download_pdf");
  const canCreateProduct =
    !permissionsLoading && permissions.has("inventory:create_product");

  // Debounced search: 250ms trailing, no leading
  const debouncedSearchChange = useMemo(
    () => debounce((value: string) => onSearchChange(value), 250, { leading: false, trailing: true }),
    [onSearchChange]
  );

  // Cancel pending debounce on unmount or when onSearchChange changes
  useEffect(() => {
    return () => {
      debouncedSearchChange.cancel();
    };
  }, [debouncedSearchChange]);

  return (
    <header className={styles.header}>
      <div className={styles.headerContent}>
        <div className={styles.leftSide}>
          <h1 className={styles.title}>Sistema de Inventarios</h1>

          <div className={styles.searchWrapper}>
            <Search className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Buscar referencias..."
              value={searchQuery}
              onChange={(e) => debouncedSearchChange(e.target.value)}
              className={styles.searchInput}
            />
          </div>

          <button
            onClick={onFilterToggle}
            className={`${styles.filterButton} ${
              isFilterOpen ? styles.active : ""
            }`}
          >
            <SlidersHorizontal className={styles.icon} />
            <span className={styles.buttonText}>Filtros</span>
          </button>
        </div>

        <div className={styles.rightSide}>
          {canDownloadPdf && (
            <Button
              variant="outlined"
              color="default"
              onClick={() => exportPdf()}
              disabled={isExporting}
            >
              {isExporting ? (
                <CircularProgress size={18} style={{ marginRight: 8 }} />
              ) : null}
              Exportar PDF
            </Button>
          )}
          {canCreateProduct && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => history.push("/proveedor/producto/nuevo")}
            >
              Nuevo Producto
            </Button>
          )}
        </div>
      </div>

      <div className={styles.mobileSearch}>
        <Search className={styles.searchIconMobile} />
        <input
          type="text"
          placeholder="Buscar referencias..."
          value={searchQuery}
          onChange={(e) => debouncedSearchChange(e.target.value)}
          className={styles.searchInputMobile}
        />
      </div>
    </header>
  );
}
